import { Card, Form, Popconfirm, Select, Space, Switch, theme } from 'antd';
import { FC } from 'react';
import { ExclamationCircle } from 'react-bootstrap-icons';

import { AppFormProps } from '@/common/interfaces';
import { defineInputStatus } from '@/common/utils';

import { RegluingIcon } from '@/common/components';
import { DomainAutoRegluingsFormFields, DomainAutoRegluingsFormInputs, DomainVisitors } from '@/modules/Domains';
import { Site } from '@/modules/Sites';

interface Props extends AppFormProps<typeof DomainAutoRegluingsFormInputs, DomainAutoRegluingsFormFields> {
  isAutoRegluingsEnabled: Site['isAutoRegluingsEnabled'];
  isVisitorSelected: boolean;
  isButtonsDisabled: boolean;
  handleToggleAutoRegluings: () => void;
  handleManualyValidateForm: () => void;
}

export const SiteDomainsAutoRegluingSettingView: FC<Props> = ({
  form,
  formErrors,
  formName,
  isAutoRegluingsEnabled,
  initialValues,
  isFormLoading,
  isVisitorSelected,
  isButtonsDisabled,
  validationSchema,
  handleToggleAutoRegluings,
  handleManualyValidateForm,
  handleFinish,
  handleFinishFailed
}) => {
  const { colorBgLayout, borderRadius, colorBorder, colorSuccess, colorError } = theme.useToken().token;

  return (
    <Card
      size="small"
      title="Автоматична переклейка"
      extra={
        <RegluingIcon
          color={ isAutoRegluingsEnabled ? colorSuccess : undefined }
          size={ 18 }
          style={{ verticalAlign: 'text-top' }}
        />
      }
    >
      <Form
        form={ form }
        name={ formName }
        initialValues={ initialValues }
        disabled={ isFormLoading }
        autoComplete="off"
        onFinish={ handleFinish }
        onFinishFailed={ handleFinishFailed }
        layout="horizontal"
      >
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              style={{ width: '100%', margin: 0 }}
              hasFeedback
              name={ DomainAutoRegluingsFormInputs.ForVisitor }
              help={ formErrors?.forVisitor }
              validateStatus={ defineInputStatus(isFormLoading, formErrors?.forVisitor) }
              rules={ validationSchema.forVisitor }
              extra={
                isAutoRegluingsEnabled ? (
                  <span style={{ fontSize: 12 }}>Щоб змінити відвідувача – спочатку вимкніть</span>
                ) : null
              }
            >
              <Select
                allowClear
                disabled={ isAutoRegluingsEnabled || isFormLoading || isButtonsDisabled }
                size="small"
                placeholder="Виберіть відвідувача"
              >
                <Select.Option value={ DomainVisitors.Any }>Будь-хто</Select.Option>
                <Select.Option value={ DomainVisitors.GoogleBot }>Google Bot</Select.Option>
              </Select>
            </Form.Item>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: 'start',
                backgroundColor: colorBgLayout,
                height: 24,
                marginTop: 4,
                paddingInline: 11,
                border: `1px solid ${ colorBorder }`,
                borderLeft: 'none',
                borderTopRightRadius: borderRadius,
                borderBottomRightRadius: borderRadius
              }}
            >
              <Form.Item
                hidden
                valuePropName="checked"
                name={ DomainAutoRegluingsFormInputs.IsEnabled }
                rules={ validationSchema.isEnabled }
                noStyle
              ></Form.Item>

              <Form.Item noStyle>
                <Popconfirm
                  disabled={ isFormLoading || !isVisitorSelected || isButtonsDisabled }
                  okType="danger"
                  okText="Так"
                  icon={ <ExclamationCircle color={ colorError }/> }
                  title="Змінити налаштування авто. переклейки?"
                  onConfirm={ handleToggleAutoRegluings }
                  placement="topLeft"
                >
                  <Switch
                    disabled={ isFormLoading || isButtonsDisabled }
                    loading={ isFormLoading }
                    size="small"
                    checked={ isAutoRegluingsEnabled }
                    onClick={ handleManualyValidateForm }
                  />
                </Popconfirm>
              </Form.Item>
            </div>
          </Space.Compact>
        </div>
      </Form>
    </Card>
  );
};
